"use client";

import React, { memo, useMemo } from "react";
import { ContentCopy, Check } from "@mui/icons-material";
import { MessageAttachment } from "./MessageAttachment";
import { Streamdown } from "streamdown";
import remarkGfm from "remark-gfm";
import type { SolaMessage } from "@/lib/sola/types";
import { cn } from "@/lib/utils";
import { useAggregatedToolState } from "@/lib/sola/hooks/useAggregatedToolState";
import { ToolRenderer, ToolRendererList } from "../ToolRenderer";
import { QuickReplies, type Suggestion } from "../ToolDisplays/suggestions/QuickRepliesDisplay";
import { useChatStore, selectDebugMode } from "@/lib/sola/store";
import { Card, Avatar, Tooltip } from "@/ui";
import { colors, spacing, radius, shadows, textStyles } from "@/ui/tokens";
import { IconButton } from "../Tool/IconButton";
import mdStyles from "./Message.module.css";

interface SolaMessageProps {
  message: SolaMessage;
  isLoading?: boolean;
  showSuggestions?: boolean;
  className?: string;
}

// Memoized component for rendering markdown content
const MemoizedMarkdownContent = memo(
  ({ content }: { content: string }) => {
    return (
      <div className={cn("sola-message-content", mdStyles.markdownContent)}>
        <Streamdown remarkPlugins={[remarkGfm]}>{content}</Streamdown>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.content === nextProps.content;
  }
);

MemoizedMarkdownContent.displayName = "MemoizedMarkdownContent";

function PureSolaMessage({ message, isLoading = false, showSuggestions = false, className }: SolaMessageProps) {
  const [copied, setCopied] = React.useState(false);
  const isUser = message.role === "user";

  const toolCalls = useAggregatedToolState(message);
  const debugMode = useChatStore(selectDebugMode);

  // Follow-up suggestions arrive as a data part after the answer.
  const followUps = useMemo(() => {
    const part = [...(message.parts ?? [])].reverse().find((p) => p.type === "data-followups") as
      | { data?: { suggestions?: Suggestion[] } }
      | undefined;
    return part?.data?.suggestions ?? [];
  }, [message.parts]);

  const textContentForCopy = useMemo(() => {
    if (!message.parts || !Array.isArray(message.parts)) {
      return "";
    }
    return message.parts
      .filter((part) => part.type === "text")
      .map((part) => part.text)
      .join("");
  }, [message.parts]);

  // Older conversations carry follow-ups as a suggestFollowUps tool call: also rendered last, outside the flow
  const suggestFollowUpsCall = useMemo(
    () => toolCalls.find(tc => tc.toolName === "suggestFollowUps"),
    [toolCalls]
  );
  const nonSuggestionToolCalls = useMemo(
    () => toolCalls.filter(tc => tc.toolName !== "suggestFollowUps"),
    [toolCalls]
  );

  const renderItems = useMemo(() => {
    if (!message.parts || !Array.isArray(message.parts)) {
      return [];
    }

    const items: Array<{
      type: 'text' | 'reasoning' | 'file' | 'tools';
      content?: string;
      part?: any;
      toolCalls?: typeof nonSuggestionToolCalls;
      index: number;
    }> = [];

    let pendingToolCalls: typeof nonSuggestionToolCalls = [];

    message.parts.forEach((part, index) => {
      // Skip suggestFollowUps — rendered separately at bottom
      if (part.type === 'tool-suggestFollowUps') {
        return;
      }

      if (part.type === 'text' && part.text) {

        if (pendingToolCalls.length > 0) {
          items.push({ type: 'tools', toolCalls: [...pendingToolCalls], index: items.length });
          pendingToolCalls = [];
        }
        items.push({ type: 'text', content: part.text, index: items.length });
      } else if (part.type === 'reasoning' && part.text && debugMode) {
        // Reasoning is a debugging aid; normal mode shows only the answer. It is
        // still in the message: the next turn's history needs it.
        if (pendingToolCalls.length > 0) {
          items.push({ type: 'tools', toolCalls: [...pendingToolCalls], index: items.length });
          pendingToolCalls = [];
        }
        items.push({ type: 'reasoning', content: part.text, index: items.length });
      } else if (part.type === 'file') {
        if (pendingToolCalls.length > 0) {
          items.push({ type: 'tools', toolCalls: [...pendingToolCalls], index: items.length });
          pendingToolCalls = [];
        }
        items.push({ type: 'file', part, index: items.length });
      } else if (part.type?.startsWith('tool-') || part.type === 'dynamic-tool') {
        const toolCallId = (part as { toolCallId?: string }).toolCallId;
        const matchingTool = nonSuggestionToolCalls.find(tc => tc.toolCallId === toolCallId);
        if (matchingTool && !pendingToolCalls.some(tc => tc.toolCallId === toolCallId)) {
          pendingToolCalls.push(matchingTool);
        }
      }
    });

    if (pendingToolCalls.length > 0) {
      items.push({ type: 'tools', toolCalls: pendingToolCalls, index: items.length });
    }

    return items;
  }, [message.parts, nonSuggestionToolCalls, debugMode]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textContentForCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const bubbleStyle = (isUserBubble: boolean): React.CSSProperties => ({
    padding: spacing[4],
    backgroundColor: isUserBubble ? colors.action.product : colors.canvas.default,
    color: isUserBubble ? colors.text.inverse : colors.text.primary,
    border: isUserBubble ? "none" : `1px solid ${colors.border.subtle}`,
    borderRadius: radius.lg,
    borderBottomRightRadius: isUserBubble ? radius.sm : radius.lg,
    borderTopLeftRadius: !isUserBubble ? radius.sm : radius.lg,
    boxShadow: shadows[1],
    position: "relative",
  });

  return (
    <div
      className={cn("group/message w-full", className)}
      data-role={message.role}
      data-testid={`sola-message-${message.role}`}
    >
      <div
        className={mdStyles.messageRow}
        style={{
          display: "flex",
          gap: spacing[4],
          alignItems: "flex-start",
          justifyContent: isUser ? "flex-end" : "flex-start",
        }}
      >
        {!isUser && (
          <Avatar
            size="sm"
            name="SOLA"
            src="/img/floats/sola.svg"
          />
        )}

        <div
          style={{
            flex: 1,
            maxWidth: isUser ? "80%" : "100%",
            display: "flex",
            flexDirection: "column",
            gap: spacing[2],
          }}
        >
          {/* Message header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: spacing[2],
              justifyContent: isUser ? "flex-end" : "flex-start",
            }}
          >
            <span
              style={{
                ...textStyles.caption,
                color: colors.text.muted,
                fontWeight: 500,
              }}
            >
              {isUser ? "You" : "SOLA"}
            </span>
            {!isUser && textContentForCopy && (
              <span className={copied ? undefined : mdStyles.copyAction}>
                <Tooltip content={copied ? "Copied!" : "Copy message"}>
                  <IconButton onClick={handleCopy}>
                    {copied ? (
                      <Check style={{ fontSize: 14, color: colors.signal.success }} />
                    ) : (
                      <ContentCopy style={{ fontSize: 14 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </span>
            )}
          </div>

          {/* Render items in chronological order */}
          {renderItems.map((item) => {
            const key = `item-${message.id}-${item.index}`;

            if (item.type === "text" && item.content) {
              return (
                <div
                  key={key}
                  style={{
                    ...bubbleStyle(isUser),
                    marginTop: item.index > 0 ? spacing[4] : 0,
                  }}
                >
                  {isUser ? (
                    <span
                      style={{
                        ...textStyles.body1,
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                      }}
                    >
                      {item.content}
                    </span>
                  ) : (
                    <MemoizedMarkdownContent content={item.content} />
                  )}
                </div>
              );
            }

            if (item.type === "reasoning" && item.content) {
              return (
                <div
                  key={key}
                  style={{
                    marginTop: item.index > 0 ? spacing[4] : 0,
                    padding: spacing[3],
                    borderLeft: `3px solid ${colors.border.subtle}`,
                    borderRadius: `0 ${radius.sm}px ${radius.sm}px 0`,
                    backgroundColor: colors.canvas.card,
                  }}
                >
                  <span
                    style={{
                      ...textStyles.label,
                      color: colors.text.disabled,
                      display: "block",
                      marginBottom: spacing[2],
                    }}
                  >
                    Reasoning
                  </span>
                  <div style={{ ...textStyles.body2, color: colors.text.muted }}>
                    <MemoizedMarkdownContent content={item.content} />
                  </div>
                </div>
              );
            }

            if (item.type === "file" && item.part) {
              return (
                <div
                  key={key}
                  style={{
                    marginTop: item.index > 0 ? spacing[4] : 0,
                    display: "flex",
                    justifyContent: isUser ? "flex-end" : "flex-start",
                  }}
                >
                  <MessageAttachment part={item.part} />
                </div>
              );
            }

            if (item.type === "tools" && item.toolCalls && item.toolCalls.length > 0) {
              return (
                <div key={key} style={{ marginTop: item.index > 0 ? spacing[4] : 0 }}>
                  <ToolRendererList
                    toolCalls={item.toolCalls}
                    isStreaming={isLoading}
                  />
                </div>
              );
            }

            return null;
          })}

          {/* Fallback for assistant messages that have tool calls but no text.
              Each tool line now surfaces its own result (see ToolThinkingDisplay),
              so we only show a minimal acknowledgement here — no per-tool summary,
              which previously duplicated the tool lines. */}
          {!isUser && !isLoading &&
           renderItems.length > 0 && !renderItems.some(item => item.type === "text") && (
            <div style={bubbleStyle(false)}>
              <span style={{ ...textStyles.body2, color: colors.text.muted, fontStyle: "italic" }}>
                Done. Let me know if you need more details.
              </span>
            </div>
          )}

          {/* Quick reply suggestions — only on the last message, after streaming */}
          {showSuggestions && followUps.length > 0 && (
            <div style={{ marginTop: spacing[2] }}>
              <QuickReplies suggestions={followUps} />
            </div>
          )}
          {showSuggestions && followUps.length === 0 && suggestFollowUpsCall && (
            <div style={{ marginTop: spacing[2] }}>
              <ToolRenderer
                toolCall={suggestFollowUpsCall}
                isStreaming={false}
              />
            </div>
          )}

          {/* Typing indicator */}
          {isLoading && (!message.parts || message.parts.length === 0) && (
            <div style={bubbleStyle(false)}>
              <div className={mdStyles.typingIndicator}>
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
        </div>

        {isUser && (
          <Avatar
            size="sm"
            name="You"
          />
        )}
      </div>
    </div>
  );
}

export function SolaMessage(props: SolaMessageProps) {
  return <PureSolaMessage {...props} />;
}

SolaMessage.displayName = "SolaMessage";
